import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStories } from '../../hooks/useStories';
import { useStreams } from '../../hooks/useStreams';
import { useAddSection } from '../../hooks/useRundown';
import { apiClient } from '../../api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Search, Plus, Check, FileText, Layers, ChevronDown, ChevronRight, FolderPlus, PlusCircle, Clock } from 'lucide-react';
import type { RundownSectionFull } from '@news-assist/shared';

interface StoryBrowserProps {
  rundownId: string;
  sections: RundownSectionFull[];
  existingStoryIds: string[];
}

interface StoryItem {
  id: string;
  title: string;
  summary: string | null;
  streamId?: string | null;
  streamTitle?: string | null;
  claimCount?: number;
  createdAt?: string;
  modifiedAt?: string;
}

function formatRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}

export function StoryBrowser({ rundownId, sections, existingStoryIds }: StoryBrowserProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [expandedStreams, setExpandedStreams] = useState<Set<string>>(new Set());
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [showNewSection, setShowNewSection] = useState(false);
  const [addingToSection, setAddingToSection] = useState<{ storyId: string; storyTitle: string } | null>(null);
  const { data: storiesData, isLoading: storiesLoading } = useStories({ hours: 18, limit: 200 });
  const { data: streamsData, isLoading: streamsLoading } = useStreams();
  const addSection = useAddSection(rundownId);
  const [addingStreamId, setAddingStreamId] = useState<string | null>(null);
  const [addingStoryId, setAddingStoryId] = useState<string | null>(null);

  const stories = (storiesData?.stories || []) as StoryItem[];
  const streams = streamsData?.streams || [];

  // Group stories by stream
  const groupedStories = useMemo(() => {
    const groups: Map<string, { title: string; stories: StoryItem[] }> = new Map();

    // Initialize with streams (in order)
    streams.forEach(stream => {
      groups.set(stream.id, { title: stream.title, stories: [] });
    });
    groups.set('unassigned', { title: 'Unassigned Stories', stories: [] });

    // Sort stories into groups
    stories.forEach(story => {
      const streamId = story.streamId || 'unassigned';
      if (!groups.has(streamId)) {
        groups.set(streamId, { title: story.streamTitle || 'Unknown Stream', stories: [] });
      }
      groups.get(streamId)!.stories.push(story);
    });

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      groups.forEach((group) => {
        group.stories = group.stories.filter(s =>
          s.title.toLowerCase().includes(searchLower)
        );
      });
    }

    // Remove empty groups
    groups.forEach((group, key) => {
      if (group.stories.length === 0) {
        groups.delete(key);
      }
    });

    return groups;
  }, [stories, streams, search]);

  const toggleStream = (streamId: string) => {
    setExpandedStreams(prev => {
      const next = new Set(prev);
      if (next.has(streamId)) {
        next.delete(streamId);
      } else {
        next.add(streamId);
      }
      return next;
    });
  };

  // Find section for a given stream
  const getSectionForStream = (streamId: string): RundownSectionFull | undefined => {
    return sections.find(s => s.streamId === streamId);
  };

  // Add stream as a new section
  const handleAddStream = async (streamId: string, title: string) => {
    setAddingStreamId(streamId);
    try {
      await addSection.mutateAsync({
        streamId: streamId === 'unassigned' ? undefined : streamId,
        title,
      });
    } catch (err) {
      console.error('Failed to add section:', err);
    } finally {
      setAddingStreamId(null);
    }
  };

  // Create a custom section (not tied to a stream)
  const handleCreateSection = async () => {
    if (!newSectionTitle.trim()) return;
    try {
      await addSection.mutateAsync({
        title: newSectionTitle.trim(),
      });
      setNewSectionTitle('');
      setShowNewSection(false);
    } catch (err) {
      console.error('Failed to create section:', err);
    }
  };

  // Add a story to a specific section
  const [error, setError] = useState<string | null>(null);
  const handleAddStoryToSection = async (storyId: string, sectionId: string) => {
    setAddingStoryId(storyId);
    setError(null);
    try {
      await apiClient.post(`/rundowns/${rundownId}/sections/${sectionId}/items`, { storyId });
      queryClient.invalidateQueries({ queryKey: ['rundown', rundownId] });
      setAddingToSection(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add story';
      console.error('Failed to add story:', err);
      setError(message);
    } finally {
      setAddingStoryId(null);
    }
  };

  // Handle clicking add on a story
  const handleAddStoryClick = (story: StoryItem) => {
    if (sections.length === 0) {
      // No sections - prompt to create one first
      setShowNewSection(true);
      return;
    }
    if (sections.length === 1 && sections[0]) {
      // Only one section - add directly
      handleAddStoryToSection(story.id, sections[0].id);
    } else {
      // Multiple sections - show picker dialog
      setAddingToSection({ storyId: story.id, storyTitle: story.title });
    }
  };

  const isLoading = storiesLoading || streamsLoading;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-white sticky top-0 z-10 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Add to Rundown</h3>
          <span className="text-xs text-gray-500">Last 18 hours</span>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search stories..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Create custom section */}
        {showNewSection ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              placeholder="Section name (e.g., Headlines)"
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSection();
                if (e.key === 'Escape') setShowNewSection(false);
              }}
            />
            <button
              onClick={handleCreateSection}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create
            </button>
            <button
              onClick={() => setShowNewSection(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowNewSection(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50"
          >
            <PlusCircle className="w-4 h-4" />
            Create Custom Section
          </button>
        )}
      </div>

      {/* Section picker dialog */}
      <Dialog open={!!addingToSection} onOpenChange={(open) => { if (!open) { setAddingToSection(null); setError(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Section</DialogTitle>
            <DialogDescription className="truncate">
              {addingToSection?.storyTitle}
            </DialogDescription>
          </DialogHeader>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {sections.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No sections available. Create a section first.
              </p>
            ) : sections.map((section) => {
              const isLoading = addingStoryId === addingToSection?.storyId;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => {
                    if (addingToSection) {
                      handleAddStoryToSection(addingToSection.storyId, section.id);
                    }
                  }}
                  disabled={isLoading}
                  className="w-full p-3 text-left rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  <div className="flex items-center gap-2">
                    <Layers className={`w-4 h-4 ${isLoading ? 'text-gray-400 animate-pulse' : 'text-purple-500'}`} />
                    <span className="font-medium text-gray-900">{section.title}</span>
                    <span className="text-xs text-gray-500 ml-auto">
                      {isLoading ? 'Adding...' : `${section.storyCount} stories`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Streams and stories */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="text-center text-gray-500 py-8">Loading stories...</div>
        ) : groupedStories.size === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {search ? 'No stories match your search' : 'No stories available'}
          </div>
        ) : (
          <div>
            {Array.from(groupedStories.entries()).map(([streamId, { title, stories: streamStories }]) => {
              const isExpanded = expandedStreams.has(streamId);
              const section = getSectionForStream(streamId);
              const hasSection = !!section;
              const isAddingStream = addingStreamId === streamId;

              return (
                <div key={streamId} className="border-b border-gray-100 last:border-b-0">
                  {/* Stream header */}
                  <div className="flex items-center gap-2 px-4 py-3 hover:bg-gray-100 transition-colors">
                    <button
                      onClick={() => toggleStream(streamId)}
                      className="flex items-center gap-2 flex-1 text-left"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <Layers className={`w-4 h-4 flex-shrink-0 ${hasSection ? 'text-green-500' : 'text-purple-500'}`} />
                      <span className="font-medium text-gray-900 flex-1 truncate">{title}</span>
                    </button>

                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {streamStories.length} stories
                    </span>

                    {hasSection ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 flex-shrink-0 px-2 py-0.5 bg-green-50 rounded">
                        <Check className="w-3 h-3" />
                        Section
                      </span>
                    ) : streamId !== 'unassigned' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddStream(streamId, title);
                        }}
                        disabled={isAddingStream}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex-shrink-0"
                        title="Add this stream as a section"
                      >
                        {isAddingStream ? '...' : (
                          <>
                            <FolderPlus className="w-3 h-3" />
                            Add Stream
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Stories in stream */}
                  {isExpanded && (
                    <div className="px-4 pb-3 space-y-1">
                      {streamStories.map((story) => {
                        const isAdded = existingStoryIds.includes(story.id);

                        return (
                          <div
                            key={story.id}
                            className={`
                              p-3 rounded-lg flex items-start gap-3 transition-colors
                              ${isAdded
                                ? 'bg-green-50 border border-green-100'
                                : 'bg-white border border-gray-100 hover:border-blue-200 hover:bg-blue-50'
                              }
                            `}
                          >
                            <FileText className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isAdded ? 'text-green-600' : 'text-gray-400'}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${isAdded ? 'text-green-700' : 'text-gray-900'}`}>
                                {story.title}
                              </p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                {story.claimCount !== undefined && story.claimCount > 0 && (
                                  <span>{story.claimCount} claims</span>
                                )}
                                {story.createdAt && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatRelativeTime(story.createdAt)}
                                  </span>
                                )}
                                {story.modifiedAt && story.modifiedAt !== story.createdAt && (
                                  <span className="text-blue-600">
                                    updated {formatRelativeTime(story.modifiedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                            {isAdded ? (
                              <span className="flex items-center gap-1 text-xs text-green-600 flex-shrink-0">
                                <Check className="w-3 h-3" />
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddStoryClick(story);
                                }}
                                disabled={addingStoryId === story.id}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex-shrink-0"
                              >
                                {addingStoryId === story.id ? '...' : <Plus className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
